/**
 * Client dell'assistente AI (BYOK).
 * Spec: docs/ai-program/10-byok-and-mcp-rework.md (Fase 2).
 *
 * L'inferenza la paga il coach con la propria chiave API: nessun credito di
 * piattaforma, nessun catalogo di modelli mantenuto da noi. La lista dei
 * modelli arriva dal provider (ADR-107), non da una costante qui dentro —
 * `aiModels.js` elencava `gpt-4o` e `claude-3-5-sonnet` ed era gia' stantia.
 */
import { fetchAPI, streamSSE } from "./utils";

/** Cosa puo' fare l'assistente per questo coach adesso (chiave, provider, limiti). */
export function getAgentStatus() {
	return fetchAPI("/ai-agent/status");
}

export function saveAgentCredentials(provider, apiKey) {
	return fetchAPI("/ai-agent/credentials", {
		method: "PUT",
		body: JSON.stringify({ provider, api_key: apiKey }),
	});
}

export function deleteAgentCredentials() {
	return fetchAPI("/ai-agent/credentials", { method: "DELETE" });
}

/**
 * La Guida AI: esempi e testi dal server (`backend/services/ai_tools/guide.py`),
 * con i permessi ricavati dal catalogo dei tool.
 */
export function getAgentGuide() {
	return fetchAPI("/ai-agent/guide");
}

/** Modelli disponibili sull'account del coach. */
export function getAgentModels() {
	return fetchAPI("/ai-agent/models");
}

/**
 * Chat agentica in streaming.
 *
 * Gli handler ricevono il testo mentre viene scritto (`onDelta`) e gli
 * strumenti mentre vengono usati (`onToolCall` / `onToolResult`). Mostrare i
 * tool non e' decorazione: e' l'unico modo per cui il coach capisca perche'
 * l'assistente ci sta mettendo tempo, invece di guardare uno spinner muto.
 */
export function streamAgentChat(body, handlers = {}) {
	const {
		onStatus,
		onDelta,
		onToolCall,
		onToolResult,
		onConfirmRequired,
		onDone,
		onError,
		signal,
	} = handlers;
	return streamSSE("/ai-agent/chat/stream", body, {
		onStatus,
		onDelta,
		onDone,
		onError,
		signal,
		onEvent: (type, data) => {
			if (type === "tool_call") onToolCall?.(data);
			else if (type === "tool_result") onToolResult?.(data);
			// Una cancellazione si è fermata al cancello del server. Arriva
			// come evento proprio e non come semplice errore di tool perché
			// il coach deve vedere un pulsante, non una riga rossa in mezzo
			// alla cronologia (backend: services/ai_agent/guard.py).
			else if (type === "confirm_required") onConfirmRequired?.(data);
		},
	});
}

/** Documenti formativi di piattaforma, per fissarli come fonte. */
export function getTrainingDocs() {
	return fetchAPI("/ai-training-docs");
}

/**
 * I punti a cui si può riportare indietro un programma, compresi quelli nati
 * dal connettore MCP — dove il lavoro succede dentro Claude o ChatGPT e non
 * c'è una nostra chat in cui mostrare «Annulla tutto».
 */
export function getRestorePoints() {
	return fetchAPI("/ai-agent/restore-points");
}

/** Annulla le scritture di una conversazione, riportando i programmi indietro. */
export function undoAgentSession(sessionId) {
	return fetchAPI("/ai-agent/undo", {
		method: "POST",
		body: JSON.stringify({ session_id: sessionId }),
	});
}

/**
 * Intensita' di ragionamento. E' la leva che decide i tempi: con trenta
 * strumenti a disposizione un modello "profondo" puo' metterci minuti per il
 * primo turno, e per estendere un programma gia' impostato non serve.
 */
export const EFFORT_LEVELS = [
	{
		value: "fast",
		label: "Rapido",
		hint: "Per estendere o ritoccare un programma che esiste già. È il caso più frequente.",
	},
	{
		value: "balanced",
		label: "Equilibrato",
		hint: "Per costruire un programma nuovo su basi chiare.",
	},
	{
		value: "deep",
		label: "Approfondito",
		hint: "Per analisi e scelte difficili. Può richiedere diversi minuti.",
	},
];

/**
 * La riga `[CONFERMA] ...` con cui l'assistente chiede il via libera.
 *
 * Nasce da un giro a vuoto: l'assistente ha chiuso un buon piano con «vuoi che
 * proceda?», il coach ha scritto «procedi», e quella parola e' ripartita come
 * una richiesta nuova — dieci minuti per rileggere cose appena lette. Ora la
 * riga diventa un pulsante che riprende la stessa sessione, e la conversazione
 * continua invece di ricominciare (servizio: ai_agent/sessions.py).
 */
const CONFIRM_LINE = /^[ \t]*\[CONFERMA\][ \t]*(.+?)[ \t]*$/im;

export function splitConfirmation(text) {
	if (!text) return { body: "", confirm: null };

	const match = text.match(CONFIRM_LINE);
	if (match) {
		// La riga non va mostrata come testo: il coach vede il pulsante.
		return { body: text.replace(match[0], "").trimEnd(), confirm: match[1] };
	}

	// Rete di sicurezza: il modello non mette sempre la riga, ma una domanda in
	// fondo al messaggio lascia il coach nello stesso punto — con un piano
	// pronto e nessun modo evidente di dire «vai».
	const lines = text.trimEnd().split("\n");
	const last = (lines[lines.length - 1] || "").trim();
	if (last.endsWith("?") && last.length <= 400) {
		return { body: text, confirm: "" };
	}

	return { body: text, confirm: null };
}

/** Etichette leggibili per i tool, per la riga di stato della chat. */
const TOOL_LABELS = {
	search_exercises: "Cerco esercizi in libreria",
	list_athletes: "Leggo gli atleti",
	get_athlete_snapshot: "Leggo la scheda atleta",
	get_athlete_maxes: "Leggo i massimali",
	get_performance_profile: "Leggo il profilo di performance",
	get_recent_results: "Leggo i risultati recenti",
	list_programs: "Cerco fra i programmi",
	get_program: "Leggo il programma",
	get_training_docs: "Consulto i documenti",
	fetch_url: "Leggo la pagina indicata",
	web_search: "Cerco fonti online",
	get_athlete_agenda: "Leggo gare e pianificazione",
	get_wellness_summary: "Controllo il recupero",
	get_coach_style: "Guardo come programmi di solito",
	get_program_structure: "Analizzo volumi e carichi",
	create_exercise: "Creo un esercizio in libreria",
	program_add_week: "Aggiungo una settimana",
	program_create_draft: "Creo il programma",
	program_add_day: "Aggiungo un giorno",
	program_validate: "Verifico il programma",
	program_finalize: "Chiudo il programma",
	program_update_week: "Riscrivo l'intestazione della settimana",
	program_update_day: "Riscrivo il giorno",
	program_remove_exercises: "Tolgo esercizi dal giorno",
	program_remove_day: "Tolgo un giorno",
	program_remove_week: "Tolgo una settimana",
};

/**
 * Gli strumenti che cancellano. Il backend è la fonte di verità (il flag
 * `destructive` nel catalogo tool); questa lista serve solo a dare alla riga
 * di stato un colore diverso mentre succede, e va tenuta allineata.
 */
const DESTRUCTIVE_TOOLS = new Set([
	"program_remove_exercises",
	"program_remove_day",
	"program_remove_week",
]);

export function isDestructiveTool(name) {
	return DESTRUCTIVE_TOOLS.has(name);
}

export function describeTool(name) {
	if (TOOL_LABELS[name]) return TOOL_LABELS[name];
	if (name?.startsWith("program_add_exercise_")) return "Aggiungo un esercizio";
	return name;
}
